import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Heading from '@tiptap/extension-heading';
import TextStyle from '@tiptap/extension-text-style';
import { CustomListItem } from '../extensions/custom-list-item';
import FontFamily from '@tiptap/extension-font-family';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import Bold from '@tiptap/extension-bold';
import Underline from '@tiptap/extension-underline';
import Blockquote from '@tiptap/extension-blockquote';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import TextAlign from '@tiptap/extension-text-align';
import { CustomColor } from '../extensions/custom-color';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import type { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Ai from '@tiptap-pro/extension-ai';
import { FontSize } from '../extensions/font-size';
import { BulletList } from '../extensions/bullet-list';
import { OrderedList } from '../extensions/ordered-list';
import { LineHeight } from '../extensions/line-height';
import { EditorVariables, PreviewContentVariables, PreviewVariables } from '../extensions/variables';
import type { VariableEntity } from '../../../../../services/entities/VariablesEntity';
import { ClearFormatting } from '../extensions/clear-formatting';
import { editorConfig } from '../../../helpers/config';
import { EditorLink, PreviewLink } from '../extensions/link';
import { SelectionHighlight, SelectionHighlightCommands } from '../extensions/selection-highlight';
import { CustomItalic } from '../extensions/custom-italic';
import { CustomBold } from '../extensions/custom-bold';
import { CustomUnderline } from '../extensions/custom-underline';
import { CustomParagraph } from '../extensions/custom-paragraph';
import { CustomHeading } from '../extensions/custom-heading';
import { TiptapAiExtension } from '../extensions/tiptap-ai';
import { Comments } from '../extensions/comments';
import { EditorInlineImage, PreviewInlineImage } from '../extensions/inline-image';
import type { CustomContextMenuOptions } from '../../CustomContextMenu/CustomContextMenuContainer.tsx';

type TiptapExtensionsConfig = {
  variables?: VariableEntity[];
  jwtToken?: string;
  documentCurrencyCode?: string;
  menuItems?: CustomContextMenuOptions;
  disableSuggestions?: boolean;
};

type TiptapExtensionsConfigForEditableCellInTableBlock = TiptapExtensionsConfig;

const defaultExtensionsForAllBlocks = [
  TextStyle,
  FontFamily,
  Blockquote,
  Bold,
  CustomBold,
  Underline,
  CustomUnderline,
  BulletList,
  Code,
  CodeBlock,
  Document,
  Dropcursor,
  Gapcursor,
  HardBreak,
  CustomHeading,
  History,
  HorizontalRule,
  CustomItalic,
  Italic,
  CustomListItem,
  OrderedList,
  Strike,
  Text,
  FontSize,
  LineHeight,
  ClearFormatting,
  Subscript,
  Superscript,
  Highlight,
  CustomColor,
  SelectionHighlightCommands,
];

const getExtensionsForPreview = ({ variables }: TiptapExtensionsConfig) => {
  return [PreviewVariables.configure({ variables }), PreviewLink, PreviewInlineImage, SelectionHighlight, Comments];
};

const getExtensionsForPreviewContent = ({ variables }: TiptapExtensionsConfig) => {
  return [PreviewContentVariables.configure({ variables }), PreviewLink, PreviewInlineImage, SelectionHighlight, Comments];
};

const getExtensionsForEditor = ({ variables, jwtToken, documentCurrencyCode, menuItems, disableSuggestions }: TiptapExtensionsConfig) => {
  const aiExtensions = [
    Ai.configure({
      appId: globalThis.env.VITE_TIPTAP_APP_ID,
      token: jwtToken,
      autocompletion: false,
      onSuccess({ editor }) {
        const { selectionStartBeforeAi, fontFamilyBeforeAi } = editor.storage.tiptapAiExtension;

        if (selectionStartBeforeAi !== null) {
          setTimeout(() => {
            const { to } = editor.state.selection;
            const chain = editor.chain().focus().setTextSelection({ from: selectionStartBeforeAi, to });

            if (fontFamilyBeforeAi) {
              chain.setFontFamily(fontFamilyBeforeAi);
            }

            chain.run();

            editor.storage.tiptapAiExtension.selectionStartBeforeAi = null;
            editor.storage.tiptapAiExtension.fontFamilyBeforeAi = null;
          }, 0);
        }
      },
    }),
    TiptapAiExtension,
  ];

  return [
    EditorVariables.configure({ variables, documentCurrencyCode, menuItems, disableSuggestions }),
    Placeholder.configure({
      placeholder: editorConfig.placeholderText,
      showOnlyWhenEditable: false,
    }),
    SelectionHighlight,
    EditorLink,
    EditorInlineImage,
    Comments,
    ...(jwtToken ? aiExtensions : []),
  ];
};

const defaultExtensionsForTextBlock = [
  ...defaultExtensionsForAllBlocks,
  CustomParagraph,
  TextAlign.configure({ types: [CustomParagraph.name, Heading.name] }),
];

export const getTextBlockExtensionsForPreview = ({ variables }: TiptapExtensionsConfig) => {
  return [...defaultExtensionsForTextBlock, ...getExtensionsForPreview({ variables })];
};

export const getTextBlockExtensionsForEditor = ({
  variables,
  jwtToken,
  documentCurrencyCode,
  menuItems,
  disableSuggestions,
}: TiptapExtensionsConfig) => {
  return [
    ...defaultExtensionsForTextBlock,
    ...getExtensionsForEditor({ variables, jwtToken, documentCurrencyCode, menuItems, disableSuggestions }),
  ];
};

const defaultExtensionsForTableBlocks = [
  ...defaultExtensionsForAllBlocks,
  CustomParagraph,
  TextAlign.configure({ types: [CustomParagraph.name, Heading.name] }),
];

export const getTableBlockExtensionsForPreview = ({ variables }: TiptapExtensionsConfig) => [
  ...defaultExtensionsForTableBlocks,
  ...getExtensionsForPreview({ variables }),
];

export const getTableBlockExtensionsForPreviewContent = ({ variables }: TiptapExtensionsConfig) => [
  ...defaultExtensionsForTableBlocks,
  ...getExtensionsForPreviewContent({ variables }),
];

export const getTableBlockExtensionsForEditor = ({
  variables,
  jwtToken,
  documentCurrencyCode,
  disableSuggestions,
}: TiptapExtensionsConfig) => [
  ...defaultExtensionsForTableBlocks,
  ...getExtensionsForEditor({ variables, jwtToken, documentCurrencyCode, disableSuggestions }),
];

export const getTableBlockExtensionsForEditableCellsInEditor = ({
  variables,
  jwtToken,
  menuItems,
  disableSuggestions,
}: TiptapExtensionsConfigForEditableCellInTableBlock) => {
  //const customParagraphExtension = getCustomParagraphThatRenderWithSpotStyles();

  return [
    ...defaultExtensionsForAllBlocks,
    CustomParagraph,
    TextAlign.configure({ types: [CustomParagraph.name, Heading.name] }),
    ...getExtensionsForEditor({ variables, jwtToken, menuItems, disableSuggestions }),
  ];
};

export const setTipTapEditorConfig = (editor: ReturnType<typeof useEditor> | null, editable: boolean, tab: 'editor' | 'preview') => {
  if (!editor) return;

  const className = tab === 'editor' ? 'py-mui-tiptap-input' : 'py-mui-tiptap-input py-mui-tiptap-input-preview';

  editor.setOptions({
    editable: tab === 'editor' && editable,
    editorProps: {
      attributes: {
        class: className,
      },
    },
  });
};
